// services/communication.service.ts

import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { TvDashboardWebSocketService } from './tv-dashboard-websocket.service';
import {
  TTvDashboardWebsocketConfig,
  TTvDashboardWebsocketRecieveMessage,
  TTvDashboardWebsocketComponentSubscribeMessage,
  TWebSocketConnectReason,
} from '@monorepo/types';
import { TvDashboardEnvService } from './tv-dashboard.env.service';

/**
 * Service responsible for managing TV Dashboard data operations and WebSocket communication.
 * Acts as a facade for WebSocket operations, providing a simplified interface for components
 * to interact with real-time dashboard data.
 */
@Injectable({
  providedIn: 'root',
})
export class TvDashboardDataService {
  constructor(
    private socketService: TvDashboardWebSocketService,
    private tvDashboardEnvService: TvDashboardEnvService,
  ) {}

  /**
   * Initializes a WebSocket connection with the specified configuration.
   * Converts configuration parameters to URL-friendly format and establishes the connection.
   * @param config - WebSocket configuration including necessary parameters
   */
  createWebSocket(config: TTvDashboardWebsocketConfig, reason: TWebSocketConnectReason) {
    const baseUrl = this.tvDashboardEnvService.getEnvVar('WS_DATA_URL');
    const params: Record<string, string> = Object.entries(config).reduce(
      (acc: Record<string, string>, [key, value]) => {
        acc[key] = Array.isArray(value) ? value.join(',') : (value as string);
        return acc;
      },
      {},
    );

    this.socketService.connect(baseUrl, params, config.subject_key, reason);
  }

  /**
   * Terminates the current WebSocket connection.
   * Alias for closeWebSocket() for backwards compatibility.
   */
  disconnectWebSocket() {
    this.socketService.disconnect();
  }

  /**
   * Closes the current WebSocket connection.
   */
  closeWebSocket() {
    this.socketService.disconnect();
  }

  /**
   * Sends a message through the WebSocket connection.
   * @param message - The component subscription message to send
   */
  sendWebSocketMessage(message: TTvDashboardWebsocketComponentSubscribeMessage) {
    this.socketService.sendMessage(message);
  }

  /**
   * Creates an Observable stream of incoming WebSocket messages.
   * @returns Observable that emits received WebSocket messages
   */
  listenToEvent(): Observable<TTvDashboardWebsocketRecieveMessage> {
    return this.socketService.listen();
  }

  /**
   * Provides an Observable of the current WebSocket connection status.
   * @returns Observable of connection status updates
   */
  getConnectionStatus() {
    return this.socketService.connectionStatus.asObservable();
  }

  /**
   * Generates a URL for retrieving image with the specified silhouette
   * @param subjectKey Subject key of the WebSocket connection
   * @param siluhoutteName Name of the silhouette
   * @returns Complete URL as a string
   */
  getImageURL(subjectKey: string, siluhoutteName: string) {
    const url = new URL(this.tvDashboardEnvService.getEnvVar('TV_DASHBOARD_IMG_URL'));

    url.searchParams.append('subject_key', subjectKey);
    url.searchParams.append('siluhoutteName', siluhoutteName);

    return url.toString();
  }
}
